/**
 * TRAC-1212: which page a chat record happened on, as the chat sends it.
 *
 * The open and every AI reply carry the host page's address, its kind and the product
 * on it. This builds that from the host's page-context message, ONCE per page and
 * BEFORE the per-page content settings decide what the agent may read: this is
 * measurement, not content, and a page with content handling off is still a page.
 *
 * The product is an allow-list (part number, brand, model, size), never the payload.
 * The backend re-applies the same allow-list (shared/page_record.py) and scrubs the
 * address there, after lifting a part number out of a tires.jsp?partnum=... query
 * string — which is why the address is sent raw here.
 */
import type { ParentPayloadV3, WorkingContext } from '../types/parentPayload';

export interface PageRecordProduct {
  part_number?: string;
  brand?: string;
  model?: string;
  size?: string;
}

export interface PageRecord {
  url?: string;
  kind?: string;
  product?: PageRecordProduct;
}

const str = (v: unknown): string | undefined => {
  if (typeof v !== 'string' && typeof v !== 'number') return undefined;
  const s = String(v).replace(/ /g, ' ').trim();
  return s ? s.slice(0, 120) : undefined;
};

/**
 * `data` is the raw TRACI_PAGE_CONTEXT message; `kind` the page label EmbedPage already
 * derived (absent, the message's own `page` / `pageKind`); `working` the working context built from it (for a results page's searched
 * size). Never throws.
 */
export function buildPageRecord(
  data: Partial<ParentPayloadV3> | null | undefined,
  kind?: string | null,
  working?: WorkingContext | null,
): PageRecord | null {
  try {
    if (!data) return null;
    const td = (data.tireDetailData ?? {}) as Record<string, unknown>;
    const tire = (td.tire ?? {}) as Record<string, unknown>;
    const searched = working?.page_selected_size ?? working?.page_searched_size;
    const searchedSize = searched?.front
      ? (searched.rear && searched.rear !== searched.front
        ? `F: ${searched.front} / R: ${searched.rear}` : searched.front)
      : undefined;
    const product: PageRecordProduct = {
      part_number: str(tire.partNumber),
      brand: str(td.make) ?? str(td.tireBrand),
      model: str(td.model) ?? str(td.tireModel),
      size: str(tire.displaySize) ?? searchedSize,
    };
    const kept = Object.fromEntries(
      Object.entries(product).filter(([, v]) => v !== undefined),
    ) as PageRecordProduct;
    const record: PageRecord = {};
    const url = typeof data.url === 'string' ? data.url.trim().slice(0, 2048) : '';
    if (url) record.url = url;
    // No label passed (the service records the host's answer before EmbedPage derives
    // one): fall back to the payload's own page label, the same one EmbedPage reads.
    const own = data as { page?: unknown; pageKind?: unknown };
    const k = str(kind ?? undefined) ?? str(own.page) ?? str(own.pageKind);
    if (k) record.kind = k;
    if (Object.keys(kept).length) record.product = kept;
    return Object.keys(record).length ? record : null;
  } catch {
    return null;
  }
}
